import React from 'react';
import styled from 'styled-components';
import { useRecoilState } from 'recoil';
import { SelectedGroupsAtomState } from '../../../recoil/ModalAtom';
import {
  splitGroupsBySection,
  getGroupTag,
  getSectionKey,
  SECTION_META,
  SECTION_CUSTOM,
  SECTION_TEMPLATE,
} from '../../utils/groupSection';

const REQUIRED_GROUP_KEY = 'infraSetting';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div``;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const SubTitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${p => p.theme.textMuted};
`;

const Section = styled.section`
  background: ${p => p.theme.bgInput};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 18px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const SectionCount = styled.span`
  font-size: 12px;
  color: ${p => p.theme.accent};
  font-weight: 600;
`;

const SectionDesc = styled.p`
  margin: 0 0 14px;
  font-size: 12px;
  color: ${p => p.theme.textMuted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media screen and (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const GroupCard = styled.button`
  position: relative;
  text-align: left;
  background: ${p => p.$selected ? p.theme.bgSelected : p.theme.bgCard};
  border: 2px solid ${p => p.$selected ? p.theme.borderSelected : p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 18px 16px;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  transition: ${p => p.theme.transition};
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 140px;

  &:hover {
    border-color: ${p => p.theme.accent};
    box-shadow: ${p => p.theme.shadowCardHover};
  }
`;

const SelectionDot = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${p => p.theme.accent};
  display: ${p => p.$visible ? 'inline-block' : 'none'};
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CardTitleText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const TypeBadge = styled.span`
  font-size: 10px;
  background: ${p => p.$variant === 'required' ? p.theme.bgRequired : p.theme.accentLight};
  color: ${p => p.$variant === 'required' ? p.theme.textRequired : p.theme.accent};
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 700;
`;

const Description = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  line-height: 1.5;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid ${p => p.theme.border};
`;

const Tag = styled.span`
  font-size: 11px;
  color: ${p => p.$required ? p.theme.textRequired : p.theme.textMuted};
  font-weight: 600;
`;

const PriceTag = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const InfoBar = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 12px 14px;
  border-radius: ${p => p.theme.radiusSm};
`;

const GROUP_DESCRIPTIONS = {
  infraSetting: '개발에 필요한 인프라, 플랫폼, 웹엔진 등 기본 환경',
  function: '시스템 기능 구현 및 비즈니스 로직 개발',
};

function getDescription(group) {
  return GROUP_DESCRIPTIONS[group.std_key] || `${group.std_value} 템플릿 개통`;
}

export default function Step1Groups({ groupList }) {
  const [selectedGroups, setSelectedGroups] = useRecoilState(SelectedGroupsAtomState);

  const toggleGroup = (key) => {
    if (key === REQUIRED_GROUP_KEY) return;
    setSelectedGroups(prev =>
      prev.includes(key) ? prev.filter(g => g !== key) : [...prev, key]
    );
  };

  const sections = splitGroupsBySection(groupList);
  const selectedCounts = {
    [SECTION_CUSTOM]: selectedGroups.filter(k => getSectionKey(k) === SECTION_CUSTOM).length,
    [SECTION_TEMPLATE]: selectedGroups.filter(k => getSectionKey(k) === SECTION_TEMPLATE).length,
  };

  const renderCard = (group) => {
    const key = group.std_key;
    const isRequired = key === REQUIRED_GROUP_KEY;
    const selected = selectedGroups.includes(key);
    const tag = getGroupTag(key);

    return (
      <GroupCard
        key={key}
        type="button"
        $selected={selected}
        $disabled={isRequired}
        onClick={() => toggleGroup(key)}
      >
        <SelectionDot $visible={selected} />
        <CardTitleRow>
          <CardTitleText>{group.std_value}</CardTitleText>
          <TypeBadge $variant={isRequired ? 'required' : 'normal'}>{tag}</TypeBadge>
        </CardTitleRow>
        <Description>{getDescription(group)}</Description>
        <FooterRow>
          <Tag $required={isRequired}>{isRequired ? '필수' : '선택'}</Tag>
          <PriceTag>{isRequired ? '0원' : '+ 금액 영향'}</PriceTag>
        </FooterRow>
      </GroupCard>
    );
  };

  const renderSection = (sectionKey) => {
    const groups = sections[sectionKey];
    if (!groups || groups.length === 0) return null;
    const meta = SECTION_META[sectionKey];
    return (
      <Section key={sectionKey}>
        <SectionHeader>
          <SectionTitle>{meta.title}</SectionTitle>
          <SectionCount>{selectedCounts[sectionKey]}개 선택됨</SectionCount>
        </SectionHeader>
        <SectionDesc>{meta.description}</SectionDesc>
        <Grid>{groups.map(renderCard)}</Grid>
      </Section>
    );
  };

  return (
    <Wrap>
      <Header>
        <Title>그룹 선택</Title>
        <SubTitle>필요한 그룹을 선택하면 다음 단계로 이동할 수 있습니다.</SubTitle>
      </Header>

      {renderSection(SECTION_CUSTOM)}
      {renderSection(SECTION_TEMPLATE)}

      <InfoBar>개발/구현 그룹은 맞춤 개발로, 공통 솔루션 그룹은 템플릿 개통으로 견적이 산정됩니다.</InfoBar>
    </Wrap>
  );
}
